import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, CircleNotchIcon as CircleNotch, SparkleIcon as Sparkle, ArrowRightIcon as ArrowRight, ReceiptIcon as Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Plan-specific "what's next" copy. Keeps the post-checkout moment
 * concrete — buyers see exactly what unlocked + the very next action.
 */
const PLAN_CELEBRATION = {
  buyer_pass: {
    title: "Buyer Pass active",
    subtitle: "Unlimited breeder messages + 6-gen pedigrees + breed alerts. Go find your dog.",
    cta: "Browse the marketplace",
    href: "/marketplace",
  },
  care_plus_dog_log: {
    title: "Dog Log unlocked",
    subtitle: "One-tap logging, smart vet warnings, sitter sharing — your dog's daily diary lives here now.",
    cta: "Open Dog Log",
    href: "/dog-log",
  },
  basic_plan_v2: {
    title: "Breeder Basic active",
    subtitle: "Register dogs, post free single-dog listings, and start your kennel page.",
    cta: "Register your first dog",
    href: "/dogs/new",
  },
  pro_plan_v2: {
    title: "Breeder Pro unlocked",
    subtitle: "Unlimited registrations, free pedigree certs, Verified Pro seal. Pays for itself in ~2 sales.",
    cta: "Open your dashboard",
    href: "/dashboard",
  },
  kennel_elite_v2: {
    title: "Kennel Elite — welcome to the top tier",
    subtitle: "8% platform fee, DNA-verified registrations, featured placement, and unlimited everything.",
    cta: "Set up your premium page",
    href: "/me/kennel",
  },
  storefront: {
    title: "Storefront unlocked",
    subtitle: "Finish Connect setup, claim your slug, brand your /store/{your-kennel} page.",
    cta: "Set up your storefront",
    href: "/me/store",
  },
};

function celebrationFor(data) {
  const plan = data?.subscription?.plan || "";
  const key = data?.subscription?.package_id || plan;
  return (
    PLAN_CELEBRATION[key] ||
    PLAN_CELEBRATION[plan] ||
    null
  );
}

export default function BillingReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", attempts: 0, data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: { message: "Missing session id" } });
      return;
    }
    let attempts = 0;
    const max = 10;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.payment_status === "paid" && data.finalized) {
          setState({ phase: "success", data });
          if (data.kind === "one_time_dog" && data.dog) {
            toast.success(`${data.dog.name} registered`);
            setTimeout(() => navigate(`/dogs/${data.dog.id}`), 1600);
          } else if (data.kind === "one_time_litter" && data.litter) {
            toast.success(`Litter registered with ${data.litter.puppy_ids.length} puppies`);
            setTimeout(() => navigate(`/litters/${data.litter.id}/confirmation`), 1600);
          } else if (data.kind === "event" && data.registration) {
            toast.success("Event registration confirmed");
            setTimeout(() => navigate(`/events/${data.registration.event_id}`), 1600);
          } else if (data.kind === "plan") {
            // Plans: keep the celebration card on-screen ~3.5s so the user
            // sees what they unlocked before the redirect. Subscription
            // checkout used to bounce in 1.2s — too fast to feel rewarding.
            const plan = data.subscription?.plan || "";
            if (plan === "storefront") {
              toast.success("Storefront unlocked — let's set yours up!");
              setTimeout(() => navigate(`/me/store`), 3500);
            } else if (plan === "storefront_featured") {
              toast.success("Your storefront is featured for 30 days");
              setTimeout(() => navigate(`/me/store`), 3500);
            } else {
              toast.success(`Plan activated`);
              setTimeout(() => navigate(`/billing`), 3500);
            }
          } else if (data.kind === "certificate" && data.dog_id) {
            toast.success("Pedigree certificate unlocked — you can download it now");
            setTimeout(() => navigate(`/dogs/${data.dog_id}`), 1600);
          } else if (data.kind === "breeding_listing" && data.dog_id) {
            toast.success("Breeding listing is live");
            setTimeout(() => navigate(`/breeding/my`), 1600);
          } else if (data.kind === "litter_marketplace_listing" && data.litter_id) {
            toast.success(
              `Litter listed on the marketplace${
                data.listing?.puppy_count ? ` — ${data.listing.puppy_count} puppies live` : ""
              }`
            );
            setTimeout(() => navigate(`/litters/${data.litter_id}/confirmation`), 1600);
          } else if (data.kind === "dog_marketplace_listing" && data.dog_id) {
            toast.success("Dog listed on the marketplace");
            setTimeout(() => navigate(`/dogs/${data.dog_id}`), 1600);
          } else if (data.kind === "marketplace_feature" && data.dog_id) {
            toast.success("Listing featured at the top of the marketplace");
            setTimeout(() => navigate(`/dogs/${data.dog_id}`), 1600);
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid" && data.status === "complete") {
          setState({ phase: "failed", data });
          return;
        }
        if (attempts >= max) {
          setState({ phase: "timeout", data });
          return;
        }
        setState({ phase: "checking", attempts, data });
        setTimeout(poll, 2000);
      } catch (err) {
        if (attempts >= max) {
          setState({ phase: "error", data: { message: err.message } });
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, navigate]);

  const celebration = state.phase === "success" && state.data?.kind === "plan"
    ? celebrationFor(state.data)
    : null;

  return (
    <AppShell>
      <div
        className="max-w-xl mx-auto bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
        data-testid="billing-return-card"
      >
        {state.phase === "checking" && (
          <>
            <CircleNotch size={48} className="text-ink mx-auto mb-4 animate-spin" />
            <h1 className="font-spectral text-3xl mb-2">Confirming payment…</h1>
            <p className="text-[#6B6B6B]">
              Stripe is sending us your confirmation. This usually takes a few seconds —
              please don't close this window.
            </p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <div className="relative inline-block mb-4">
              <CheckCircle
                size={56}
                weight="duotone"
                className="text-[#0F8043] mx-auto"
                data-testid="billing-return-success-icon"
              />
              <Sparkle size={20} weight="fill" className="text-[#C9A227] absolute -top-1 -right-2" />
            </div>
            <h1 className="font-spectral text-3xl mb-2" data-testid="billing-return-success-title">
              {celebration ? celebration.title : "Payment confirmed"}
            </h1>
            <p className="text-[#3d3d3d] max-w-md mx-auto mb-6">
              {celebration?.subtitle || "Thanks — you're all set. Redirecting you now."}
            </p>
            {celebration && (
              <div className="flex flex-col sm:flex-row gap-2 justify-center items-stretch">
                <Link to={celebration.href} className="block">
                  <Button
                    className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-6 h-11 w-full sm:w-auto"
                    data-testid="billing-return-primary-cta"
                  >
                    {celebration.cta}
                    <ArrowRight size={14} weight="bold" className="ml-1.5" />
                  </Button>
                </Link>
                <Link to="/billing" className="block">
                  <Button
                    variant="outline"
                    className="rounded-full px-6 h-11 w-full sm:w-auto"
                    data-testid="billing-return-secondary-cta"
                  >
                    <Receipt size={14} weight="bold" className="mr-1.5" />
                    Manage subscription
                  </Button>
                </Link>
              </div>
            )}
            {!celebration && (
              <p className="text-xs text-[#8a8a8a]">Redirecting…</p>
            )}
          </>
        )}
        {(state.phase === "failed" || state.phase === "timeout" || state.phase === "error") && (
          <>
            <XCircle size={48} weight="duotone" className="text-crimson mx-auto mb-4" />
            <h1 className="font-spectral text-3xl mb-2">Payment not completed</h1>
            <p className="text-[#6B6B6B] mb-2">
              {state.phase === "timeout"
                ? "We couldn't confirm the payment in time. If you completed it, please refresh."
                : "The payment was cancelled or expired."}
            </p>
            <p className="text-xs text-[#8a8a8a] mb-6">
              No charge was made. You can try again or pick a different plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={() => navigate("/billing")}
                className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-6"
                data-testid="billing-return-back-btn"
              >
                Back to billing
              </Button>
              <Link to="/services">
                <Button variant="outline" className="rounded-full px-6 w-full sm:w-auto">
                  Compare plans
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
